import type {
  DecisionRecord,
  GateRecord,
  NodeStateType,
  WorkflowNode,
} from '../../../types/backend';

/**
 * The slice of a run the stage view reads. WorkflowRun satisfies it as is, so callers pass
 * `detail.run` straight through.
 */
export interface StagesRun {
  current_layer?: number;
  node_states?: Record<string, NodeStateType>;
  gates?: Pick<GateRecord, 'node_id' | 'resolution'>[];
  decisions?: Pick<DecisionRecord, 'node_id' | 'chosen_condition' | 'reason'>[];
  evidence?: Record<string, string>;
  facts?: Record<string, Record<string, string>>;
}

export type StepStatus = 'done' | 'current' | 'waiting' | 'pending' | 'skipped' | 'failed';

export interface Stage {
  layer: number;
  nodes: WorkflowNode[];
}

/** The plan as ordered stages. A stage is a layer: every step in it finishes before the next. */
export const toStages = (nodes: WorkflowNode[]): Stage[] => {
  const byLayer = new Map<number, WorkflowNode[]>();
  nodes.forEach((node) => {
    const members = byLayer.get(node.layer) ?? [];
    members.push(node);
    byLayer.set(node.layer, members);
  });
  return [...byLayer.keys()]
    .sort((a, b) => a - b)
    .map((layer) => ({ layer, nodes: byLayer.get(layer) ?? [] }));
};

export const stepStatus = (node: WorkflowNode, run: StagesRun): StepStatus => {
  const state = run.node_states?.[node.id] ?? 'pending';
  if (state === 'done') return 'done';
  if (state === 'pruned') return 'skipped';
  if (state === 'failed') return 'failed';
  const gateOpen = (run.gates ?? []).some((g) => g.node_id === node.id && !g.resolution);
  if (gateOpen) return 'waiting';
  return node.layer === run.current_layer ? 'current' : 'pending';
};

export const stageSkipped = (stage: Stage, run: StagesRun): boolean =>
  stage.nodes.every((node) => run.node_states?.[node.id] === 'pruned');

/** How a stage reads in one line: unordered unless some step waits on a sibling. */
export const stageSummary = (nodes: WorkflowNode[]): string | null => {
  if (nodes.length < 2) return null;
  const ordered = nodes.some((n) => (n.depends_on ?? []).length > 0);
  return ordered ? `${nodes.length} steps, in the order marked` : `${nodes.length} steps, any order`;
};
